import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faChevronDown,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useCurrentEditor, useEditorState } from '@tiptap/react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Align left', icon: faAlignLeft },
  { value: 'center', label: 'Align center', icon: faAlignCenter },
  { value: 'right', label: 'Align right', icon: faAlignRight },
  { value: 'justify', label: 'Justify', icon: faAlignJustify },
] as const;

export function AlignSelector() {
  const { editor } = useCurrentEditor();

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const ed = ctx.editor;
      if (!ed) {
        return {
          isAlignCenter: false,
          isAlignRight: false,
          isAlignJustify: false,
        };
      }

      const isAlignCenter = ed.isActive({ textAlign: 'center' });
      const isAlignRight = ed.isActive({ textAlign: 'right' });
      const isAlignJustify = ed.isActive({ textAlign: 'justify' });

      return {
        isAlignCenter,
        isAlignRight,
        isAlignJustify,
      };
    },
  });

  if (!editor || !editorState) {
    return null;
  }

  const currentValue: (typeof ALIGN_OPTIONS)[number]['value'] = editorState.isAlignCenter
    ? 'center'
    : editorState.isAlignRight
      ? 'right'
      : editorState.isAlignJustify
        ? 'justify'
        : 'left';

  const currentOption = ALIGN_OPTIONS.find((opt) => opt.value === currentValue) ?? ALIGN_OPTIONS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="secondary"
          className="gap-1 px-2"
          aria-label={`Text alignment: ${currentOption.label}`}
          type="button"
        >
          <Icon icon={currentOption.icon} />
          <Icon icon={faChevronDown} className="size-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-rich-text-editor-ui="format-toolbar-submenu"
        align="start"
        className="min-w-48"
      >
        <DropdownMenuRadioGroup
          value={currentValue}
          onValueChange={(value) => {
            editor.chain().focus().setTextAlign(value).run();
          }}
        >
          {ALIGN_OPTIONS.map((opt) => (
            <DropdownMenuRadioItem key={opt.value} value={opt.value}>
              <Icon icon={opt.icon} />
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
